import type { LegacyRiskGapStatus } from '@ctw/shared/api/fhir/models/risk-gap';
import { Button } from '@ctw/shared/components/button';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { faClock, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import {
  RiskGapStatusDialogContent,
  type RiskGapStatusDialogStatus,
} from './risk-gap-status-dialog';

export interface RiskGapStatusFormV2Props {
  initialNote: string;
  currentStatus: LegacyRiskGapStatus;
  onSave?: (
    note: string,
    status: RiskGapStatusDialogStatus,
    substatuses?: Array<string>,
  ) => void | Promise<void>;
}

export const RiskGapStatusFormV2 = ({
  initialNote,
  currentStatus,
  onSave,
}: RiskGapStatusFormV2Props) => {
  const [note, setNote] = useState<string>(initialNote);
  const [_loading, setLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Sync note after save + refetch) so the textarea shows the latest note
  useEffect(() => {
    setNote(initialNote);
  }, [initialNote]);

  const telemetry = useTelemetry();
  const { openModal, closeModal } = useModal({
    boundaryName: 'StatusDialogModal',
    layout: { fullbleed: false, mode: 'fit', overflowBehavior: 'scroll', showCloseButton: false },
  });

  const isExpectedToClose =
    currentStatus === 'expected-to-close-1st-party' ||
    currentStatus === 'expected-to-close-3rd-party';

  const canTransitionTo = (targetStatus: LegacyRiskGapStatus) =>
    currentStatus !== targetStatus && !isExpectedToClose;

  const showAcceptButton = canTransitionTo('accepted-for-review');
  const showDismissButton = canTransitionTo('dismissed');
  const showPendingButton = canTransitionTo('pending');
  const showReopenButton = canTransitionTo('open-gap');

  // TODO Useful with GAP-1153
  const handleNoteChange = (note: string) => {
    setNote(note);
  };

  const handleCancel = () => {
    setIsConfirming(false);
    closeModal();
  };

  const handleConfirmWithSubstatuses = async (
    callback:
      | ((
          notes: string,
          status: RiskGapStatusDialogStatus,
          substatuses?: Array<string>,
        ) => void | Promise<void>)
      | undefined,
    status: RiskGapStatusDialogStatus,
    substatuses: Array<string>,
    pendingNote: string,
  ) => {
    if (!callback) {
      return;
    }
    setLoading(true);
    try {
      await Promise.resolve(callback(pendingNote, status, substatuses));
      closeModal();
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (status: RiskGapStatusDialogStatus) => {
    setIsConfirming(true);
    openModal({
      telemetry,
      action: {
        content: {
          body: (
            <RiskGapStatusDialogContent
              status={status}
              note={note}
              onConfirm={(substatuses, pendingNote) =>
                handleConfirmWithSubstatuses(onSave, status, substatuses, pendingNote)
              }
              onCancel={handleCancel}
            />
          ),
        },
      },
    });
  };

  return (
    <div className={tw`flex h-full w-full flex-col gap-4 transition-all duration-100`}>
      <textarea
        id="risk-gap-status-note"
        placeholder="Add Note..."
        onChange={(event) => {
          if (!isConfirming) {
            handleNoteChange(event.currentTarget.value);
          }
        }}
        value={note}
        rows={4}
        className={tw`w-full resize-none border border-meta-background shadow-none focus:outline-double focus:outline-meta-background`}
      />
      <div className={tw`flex w-full flex-wrap gap-3`}>
        {showReopenButton ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={tw`border bg-white text-content-main shadow-none`}
            onClick={() => handleAction('open-gap')}
          >
            <FontAwesomeIcon
              icon={faArrowRotateLeft}
              className={tw`relative top-[-1] mr-1.5 h-3 `}
            />
            Reopen
          </Button>
        ) : null}
        {showAcceptButton ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={tw`border bg-white text-content-main shadow-none`}
            onClick={() => handleAction('accepted-for-review')}
          >
            <FontAwesomeIcon icon={faThumbsUp} className={tw`relative top-[-1] mr-1.5 h-3`} />
            Accept
          </Button>
        ) : null}
        {showDismissButton ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={tw`shadow-none`}
            onClick={() => handleAction('dismissed')}
          >
            <FontAwesomeIcon
              icon={faThumbsDown}
              className={tw`relative top-[-1] mr-1.5 h-3 scale-x-[-1.1] scale-y-[1.1]`}
            />
            Dismiss
          </Button>
        ) : null}
        {showPendingButton ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={tw`shadow-none`}
            onClick={() => handleAction('pending')}
          >
            <FontAwesomeIcon icon={faClock} className={tw`relative top-[-1] h-3`} />
          </Button>
        ) : null}
      </div>
    </div>
  );
};
